import React, { useRef, useEffect, useState } from 'react';
import { useIntl } from 'gatsby-plugin-intl';
import { graphql, useStaticQuery } from 'gatsby';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { getImage } from 'gatsby-plugin-image';
import { convertToBgImage } from 'gbimage-bridge';
import { isBrowser } from 'react-device-detect';
import { formAnimation } from 'theme/Animations';
import Input from '../../components/atoms/Input';
import FileUploader from '../../components/atoms/FileUploader';
import Spinner from '../../components/FormLoader/Spinner';
import Checkbox from '../../components/atoms/Checkbox';
import Success from '../../components/FormLoader/Success';
import {
  StyledSectionWrapper,
  StyledFormWrapper,
  StyledForm,
  StyledInputRow,
  StyledTitle,
  StyledGroupTitle,
  StyledSubmitBtn,
  StyledFormInnerWrapper,
  StyledBlurWrapper,
} from './InquiryFormStyles';

const InquiryForm = () => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  let container = useRef(null);
  let form = useRef(null);

  const data = useStaticQuery(graphql`
    {
      file(name: { eq: "inquiry-form-bg" }) {
        childImageSharp {
          gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
        }
      }
    }
  `);

  const gatsbyImage = getImage(data.file);
  const bgImage = convertToBgImage(gatsbyImage);

  const telRegExp = /^\+?[0-9]{3}-?[0-9]{6,12}$/;
  const DisplayingErrorMessagesSchema = Yup.object().shape({
    name: Yup.string()
      .min(2, intl.formatMessage({ id: 'form_field_min' }))
      .max(50, intl.formatMessage({ id: 'form_field_max' }))
      .required(intl.formatMessage({ id: 'form_field_required' })),
    tel: Yup.string()
      .matches(telRegExp, intl.formatMessage({ id: 'form_tel_error' }))
      .required(intl.formatMessage({ id: 'form_field_required' })),
    email: Yup.string()
      .email(intl.formatMessage({ id: 'form_email_error' }))
      .required(intl.formatMessage({ id: 'form_field_required' })),
    lastName: Yup.string()
      .min(2, intl.formatMessage({ id: 'form_field_min' }))
      .max(50, intl.formatMessage({ id: 'form_field_max' }))
      .required(intl.formatMessage({ id: 'form_field_required' })),
    message: Yup.string()
      .min(10, intl.formatMessage({ id: 'form_field_min' }))
      .max(2000, intl.formatMessage({ id: 'form_field_max' })),
    companyName: Yup.string()
      .min(1, intl.formatMessage({ id: 'form_field_min' }))
      .max(50, intl.formatMessage({ id: 'form_field_max' })),
    nip: Yup.string()
      .matches(/^[0-9]+$/, intl.formatMessage({ id: 'form_field_number_only' }))
      .min(10, `${intl.formatMessage({ id: 'form_field_char_exacly' })}10`)
      .max(10, `${intl.formatMessage({ id: 'form_field_char_exacly' })}10`),
    country: Yup.string()
      .min(2, intl.formatMessage({ id: 'form_field_min' }))
      .max(50, intl.formatMessage({ id: 'form_field_max' })),
    file: Yup.mixed()
      .nullable()
      .notRequired()
      .test(
        'fileSize',
        `${intl.formatMessage({ id: 'form_file_too_big' })}10MB`,
        value => !value || (value && value.size <= 10000000)
      ),
    rules: Yup.boolean().oneOf(
      [true],
      intl.formatMessage({ id: 'form_filed_accept' })
    ),
  });

  useEffect(() => {
    if (isBrowser) {
      formAnimation(container, form);
    }
  }, []);

  return (
    <StyledSectionWrapper Tag="div" {...bgImage}>
      <StyledFormWrapper
        ref={el => {
          container = el;
        }}
      >
        <Formik
          initialValues={{
            name: '',
            lastName: '',
            tel: '',
            email: '',
            message: '',
            companyName: '',
            nip: '',
            country: '',
            file: false,
            rules: false,
          }}
          validationSchema={DisplayingErrorMessagesSchema}
          onSubmit={(values, { resetForm }) => {
            setSubmitting(true);
            setLoading(true);

            const formData = new FormData();
            formData.append('first_name', values.name);
            formData.append('last_name', values.lastName);
            formData.append('phone', values.tel);
            formData.append('email', values.email);
            formData.append('remarks', values.message);
            formData.append('company', values.companyName);
            formData.append('nip', values.nip);
            formData.append('country', values.country);

            formData.append('file', values.file);
            // eslint-disable-next-line no-undef
            fetch(`${process.env.GATSBY_API_URL}/ask-for-offer`, {
              method: 'POST',
              body: formData,
            })
              .then(response => response.json())
              .then(content => {
                // eslint-disable-next-line
                if (parseInt(content.statusCode) === 200) {
                  setTimeout(() => {
                    setLoading(false);
                    setSuccess(true);
                    resetForm();
                    setTimeout(() => {
                      setSuccess(false);
                      setSubmitting(false);
                    }, 3000);
                  }, 3000);
                } else {
                  setTimeout(() => {
                    setLoading(false);
                    setSuccess(false);
                    setSubmitting(false);
                  }, 3000);
                  // eslint-disable-next-line no-console
                  console.error(content.error);
                }
              })
              .catch(error => {
                setTimeout(() => {
                  setLoading(false);
                  setSuccess(false);
                  setSubmitting(false);
                }, 3000);
                // eslint-disable-next-line no-console
                console.error(error);
              });
          }}
        >
          {({
            errors,
            touched,
            handleChange,
            handleBlur,
            values,
            handleSubmit,
            setFieldValue,
          }) => (
            <StyledFormInnerWrapper
              ref={el => {
                form = el;
              }}
            >
              <StyledBlurWrapper>
                <StyledForm blur={submitting} onSubmit={handleSubmit}>
                  <StyledTitle>
                    {intl.formatMessage({ id: 'inquiry_form_title' })}
                  </StyledTitle>

                  <StyledGroupTitle>
                    {intl.formatMessage({ id: 'form_contact_data' })}
                  </StyledGroupTitle>

                  <StyledInputRow>
                    <Input
                      type="text"
                      name="name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name}
                      label={intl.formatMessage({ id: 'form_label_name' })}
                      errorMessage={touched.name && errors.name}
                      required
                    />

                    <Input
                      type="text"
                      name="lastName"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.lastName}
                      label={intl.formatMessage({ id: 'form_label_last_name' })}
                      errorMessage={touched.lastName && errors.lastName}
                      required
                    />
                  </StyledInputRow>

                  <StyledInputRow>
                    <Input
                      type="tel"
                      name="tel"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.tel}
                      label={intl.formatMessage({ id: 'form_label_tel' })}
                      errorMessage={touched.tel && errors.tel}
                      required
                    />

                    <Input
                      type="email"
                      name="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      label={intl.formatMessage({ id: 'form_label_email' })}
                      errorMessage={touched.email && errors.email}
                      required
                    />
                  </StyledInputRow>

                  <Input
                    type="textarea"
                    name="message"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.message}
                    label={intl.formatMessage({ id: 'inquiry_form_remarks' })}
                    errorMessage={touched.message && errors.message}
                  />

                  <StyledGroupTitle>
                    {intl.formatMessage({ id: 'form_company_data' })}
                  </StyledGroupTitle>

                  <StyledInputRow>
                    <Input
                      type="text"
                      name="companyName"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.companyName}
                      label={intl.formatMessage({
                        id: 'form_label_company_name',
                      })}
                      errorMessage={touched.companyName && errors.companyName}
                    />

                    <Input
                      type="text"
                      name="nip"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.nip}
                      label={intl.formatMessage({ id: 'form_label_nip' })}
                      errorMessage={touched.nip && errors.nip}
                    />
                  </StyledInputRow>

                  <StyledInputRow>
                    <Input
                      type="text"
                      name="country"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.country}
                      label={intl.formatMessage({ id: 'form_label_country' })}
                      errorMessage={touched.country && errors.country}
                    />
                  </StyledInputRow>

                  <FileUploader
                    name="file"
                    onFileSelect={setFieldValue}
                    onBlur={handleBlur}
                    value={values.file}
                    errorMessage={touched.file && errors.file}
                  />

                  <Checkbox
                    name="rules"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.rules}
                    label={intl.formatMessage({ id: 'form_label_rules' })}
                    errorMessage={touched.rules && errors.rules}
                  >
                    <p>
                      {intl.formatMessage({
                        id: 'form_label_rules.1',
                      })}
                      <a href="mailto:info@holika.pl">info@holika.pl</a>
                    </p>
                  </Checkbox>

                  <StyledSubmitBtn type="submit">
                    {intl.formatMessage({ id: 'form_submit' })}
                  </StyledSubmitBtn>
                </StyledForm>
              </StyledBlurWrapper>

              {loading && <Spinner />}
              {success && <Success />}
            </StyledFormInnerWrapper>
          )}
        </Formik>
      </StyledFormWrapper>
    </StyledSectionWrapper>
  );
};

export default InquiryForm;
